import { z } from 'zod';
import { id, paginationQuery, paginatedResult } from './common.js';

export const rebookingStatus = z.enum(['requested', 'offered', 'declined', 'withdrawn', 'closed']);
export const rebookingSide = z.enum(['professional', 'office']);
const calendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'Enter a valid calendar date.');
const dates = { preferredFrom: calendarDate.nullable(), preferredTo: calendarDate.nullable() };
export const rebookingCreate = z
  .object({
    sourceBookingId: id,
    ...dates,
    message: z.string().trim().min(1).max(2000),
    clientRequestId: z.string().uuid(),
  })
  .strict()
  .refine((v) => !v.preferredFrom || !v.preferredTo || v.preferredFrom <= v.preferredTo, {
    message: 'The end date must be on or after the start date.',
  });
export type RebookingCreate = z.infer<typeof rebookingCreate>;
export const rebookingMutation = z
  .object({
    action: z.enum(['offer', 'reply', 'decline', 'withdraw', 'close']),
    expectedRevision: z.number().int().positive(),
    clientRequestId: z.string().uuid(),
    message: z.string().trim().min(1).max(2000),
    shiftId: id.optional(),
  })
  .strict()
  .refine((v) => v.action !== 'offer' || !!v.shiftId, { message: 'Choose the proposed shift.' });
export type RebookingMutation = z.infer<typeof rebookingMutation>;
export const rebookingQuery = paginationQuery
  .extend({
    search: z.string().trim().max(100).optional(),
    status: rebookingStatus.optional(),
    locationId: id.optional(),
  })
  .strict();
export const rebookingSource = z.object({
  sourceBookingId: id,
  locationId: id,
  locationName: z.string(),
  professionalId: id,
  professionalName: z.string(),
  role: z.string(),
  sourceStartsAt: z.string(),
  sourceEndsAt: z.string(),
  timezone: z.string(),
  callerSide: rebookingSide,
  canWrite: z.boolean(),
  canStart: z.boolean().optional(),
});
export type RebookingSource = z.infer<typeof rebookingSource>;
export const rebookingCandidate = z.object({
  shiftId: id,
  startsAt: z.string(),
  endsAt: z.string(),
  payRateCents: z.number().nullable(),
  bookingMode: z.enum(['accept', 'request']),
  eligible: z.boolean(),
  reason: z.string().nullable(),
  bookingId: id.nullable(),
  bookingStatus: z.string().nullable(),
});
export const rebookingContext = z.object({
  source: rebookingSource,
  shifts: paginatedResult(rebookingCandidate),
});
export const rebookingRecord = rebookingSource.extend({
  id,
  createdByUserId: id,
  initiatorSide: rebookingSide,
  status: rebookingStatus,
  revision: z.number().int().positive(),
  ...dates,
  message: z.string(),
  offeredShiftId: id.nullable(),
  offeredBookingId: id.nullable(),
  offeredBookingStatus: z.string().nullable(),
  offerAvailable: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RebookingRecord = z.infer<typeof rebookingRecord>;
export const rebookingEvent = z.object({
  id,
  kind: z.string(),
  actorName: z.string(),
  actorSide: rebookingSide,
  message: z.string(),
  revision: z.number().int().positive(),
  recordedAt: z.string(),
});
export const rebookingPage = paginatedResult(rebookingRecord);
export const rebookingSourcePage = paginatedResult(rebookingSource);
export const rebookingDetail = z.object({
  request: rebookingRecord,
  events: paginatedResult(rebookingEvent),
});
